import React, { useContext } from "react";
import { YearContext } from "../../../contexts/yearContext";
import db from "../../../services/query";
import AriaReport, {
  buildPrintDocumentTitle,
  ConfirmMark,
  faDigits,
  ReportSignatures,
  toShamsiDate,
  usePrintData,
} from "./AriaReport";

const stateLabel = (value) => {
  if (value == null || value === "") return "ثبت نشده";
  if (Number(value) === 1) return "حاضر";
  if (Number(value) === 2) return "غیر حاضر";
  return String(value);
};

export default function DailyAttendancePrint({ match, location, kind }) {
  const { entryYear } = useContext(YearContext) || {};
  const date = match.params.date;
  const queryYear = new URLSearchParams(location.search).get("entryYearId") || entryYear || "";
  const teacher = kind === "teachers";
  const shamsi = toShamsiDate(date);
  const { loading, error, data, summary } = usePrintData(
    async () => {
      const params = new URLSearchParams({ kind, date, entryYearId: String(queryYear) });
      const { data: body } = await db.getAll(`mis-parity/reports/daily-attendance?${params}`);
      return body;
    },
    [kind, date, queryYear]
  );
  const sections = Array.isArray(data) ? data : [];
  const summaries = summary?.summaries || [];
  const totals = summary?.totals || { present_count: 0, absent_count: 0 };
  const present = Number(totals.present_count || 0);
  const absent = Number(totals.absent_count || 0);
  const total = present + absent;
  const percent = total ? Math.round((present / total) * 100) : 0;
  const reportTitle = teacher ? "گزارش حاضری استادان" : "گزارش حاضری کارمندان";

  if (loading) return <p className="aria-report-status">در حال آماده‌سازی گزارش…</p>;
  if (error) return <p className="aria-report-status" role="alert">{error}</p>;

  return (
    <AriaReport
      className="daily-attendance-report"
      pageMargin="8mm"
      documentTitle={buildPrintDocumentTitle([reportTitle, date, shamsi])}
      rightLines={[
        reportTitle,
        <span key="dates" className="daily-report-dates">
          <span className="daily-report-date-gregorian" dir="ltr">
            {faDigits(date)}
          </span>
          {shamsi ? (
            <span className="daily-report-date-shamsi" dir="ltr">
              {faDigits(shamsi)}
            </span>
          ) : null}
        </span>,
      ]}
      leftLines={
        teacher
          ? ["وزارت تحصیلات عالی", "پوهنتون آریا", sections[0]?.data?.[0]?.faculty_name || "", "مدیریت تدریسی"]
          : ["وزارت تحصیلات عالی", "پوهنتون آریا", "معاونیت اداری", "مدیریت منابع بشری"]
      }
    >
      {sections.map((section, index) => (
        <section className="daily-attendance-section" key={`${section.title}-${index}`}>
          <h2 className="section-head">{section.title}</h2>
          <table className="aria-report-table compact-people">
            <thead>
              <tr>
                <th>شماره</th>
                <th>اسم</th>
                <th>زمان ورود</th>
                <th>زمان خروج</th>
                <th className="confirm-col">{teacher ? <>تایید مدیر<br />پوهنحی</> : <>تایید مدیر<br />منابع بشری</>}</th>
                <th className="confirm-col">{teacher ? <>تایید ریس<br />پوهنحی</> : <>تایید معاون<br />اداری</>}</th>
                <th>حاضری</th>
                <th>ملاحظات</th>
              </tr>
            </thead>
            <tbody>
              {(section.data || []).map((row, rowIndex) => (
                <tr key={row.attendance_id || row.staff_attendance_id || rowIndex}>
                  <td>{faDigits(rowIndex + 1)}</td>
                  <td className="name-col">{teacher ? row.teacher_name : row.staff_name}</td>
                  <td className="nowrap" dir="ltr">{row.check_in_time || "—"}</td>
                  <td className="nowrap" dir="ltr">{row.check_out_time || "—"}</td>
                  <td className="center">
                    <ConfirmMark value={teacher ? row.confirmed_by_admin : row.confirmed_by_hr} />
                  </td>
                  <td className="center">
                    <ConfirmMark value={teacher ? row.confirmed_by_hod : row.confirmed_by_head} />
                  </td>
                  <td>{stateLabel(row.attendance_state)}</td>
                  <td>{row.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
      <div className="daily-report-footer">
        <table className="aria-report-table summary-mini">
          <thead>
            <tr>
              <th>بخش</th>
              <th>حاضر</th>
              <th>غیر حاضر</th>
            </tr>
          </thead>
          <tbody>
            {summaries.map((row, index) => (
              <tr key={`${row.name}-${index}`}>
                <td>{row.name || row.section_name || row.teacher_name}</td>
                <td>{faDigits(row.present_count || 0)}</td>
                <td>{faDigits(row.absent_count || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="daily-report-chart">
          <div className="daily-report-pie" style={{ "--present": `${percent}%` }} />
          <div>
            <p>
              <b>{faDigits(present)}</b> حاضر
            </p>
            <p>
              <b>{faDigits(absent)}</b> غیر حاضر
            </p>
          </div>
        </div>
      </div>
      <ReportSignatures labels={teacher ? ["مدیر تدریسی", "ریس پوهنحی"] : ["مدیر منابع بشری", "معاون اداری"]} />
    </AriaReport>
  );
}
